import DOMPurify from 'isomorphic-dompurify'

// Constants
import { REGION } from '@/constants/region'
import { ROUTE_PATH } from '@/constants/routePath'

const REGIONS = Object.values(REGION)

/**
 * 幫助中心的站點（分類各自掛在 TW / HK / KR / JP 站）。
 * SSR 拿不到使用者位置，所以用 ?country_code= 或語系決定：結果穩定、搜尋引擎抓得到同一份內容。
 */
export const getHelpCountry = (locale?: string, queryCountry?: unknown): string => {
  const fromQuery = typeof queryCountry === 'string' ? queryCountry.toUpperCase() : ''
  if (REGIONS.includes(fromQuery)) return fromQuery

  switch (locale) {
    case 'zh-hk':
    case 'en-hk':
      return REGION.HK
    case 'ko-kr':
      return REGION.KR
    case 'ja-jp':
      return REGION.JP
    default:
      return REGION.TW
  }
}

/** 網址帶了 ?country_code= 時，站內連結都要帶著，否則一點就跳回語系預設的站 */
const withCountry = (path: string, countryQuery?: string) => (countryQuery ? `${path}?country_code=${encodeURIComponent(countryQuery)}` : path)

export const helpHomePath = (countryQuery?: string) => withCountry(ROUTE_PATH.HELP, countryQuery)

export const helpCollectionPath = (id: number, countryQuery?: string) =>
  withCountry(ROUTE_PATH.HELP_COLLECTION.replace('[id]', String(id)), countryQuery)

export const helpArticlePath = (id: number, countryQuery?: string) => withCountry(ROUTE_PATH.HELP_ARTICLE.replace('[id]', String(id)), countryQuery)

export const helpTermsPath = (countryQuery?: string) => withCountry(ROUTE_PATH.HELP_TERMS, countryQuery)

/** 條款頁沿用既有的 /terms/* 網址 */
export const legalDocPath = (pageKey: string, countryQuery?: string) => withCountry(`/terms/${pageKey}`, countryQuery)

export const helpSearchPath = (q: string, countryQuery?: string) =>
  `${ROUTE_PATH.HELP_SEARCH}?q=${encodeURIComponent(q)}${countryQuery ? `&country_code=${encodeURIComponent(countryQuery)}` : ''}`

/** 路由參數取文章 / 分類 id；允許 /help/articles/123-標題 這種帶 slug 的寫法 */
export const parseHelpId = (raw: unknown): number => {
  const id = Number.parseInt(String(raw ?? ''), 10)
  return Number.isFinite(id) && id > 0 ? id : 0
}

export interface HelpTocItem {
  id: string
  text: string
}

const stripTags = (html: string) =>
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim()

/**
 * 文章 HTML：sanitize → 小標加錨點（目錄用）→ 外部連結開新分頁。
 * 內容來自後台編輯器（只產生 h2 / p / 清單 / 引用 / 連結 / 圖片），仍一律過 DOMPurify。
 */
export const prepareArticleHtml = (content: string): { html: string; toc: HelpTocItem[] } => {
  const clean = DOMPurify.sanitize(content || '', { ADD_ATTR: ['target'] })
  const toc: HelpTocItem[] = []

  const withAnchors = clean.replace(/<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/g, (_, _attrs, inner: string) => {
    const id = `section-${toc.length + 1}`
    const text = stripTags(inner)
    if (text) toc.push({ id, text })
    return `<h2 id="${id}">${inner}</h2>`
  })

  const html = withAnchors.replace(/<a\s+href="(https?:\/\/[^"]+)"([^>]*)>/g, (match, href: string, rest: string) =>
    /target=/.test(rest) ? match : `<a href="${href}" target="_blank" rel="noopener noreferrer"${rest}>`,
  )

  return { html, toc }
}

/** meta description：摘要優先，沒有就取內文開頭 */
export const articleDescription = (summary: string, content: string, max = 150) => {
  const text = (summary || stripTags(content || '')).replace(/\s+/g, ' ').trim()
  return text.length > max ? `${text.slice(0, max)}…` : text
}

/**
 * 查無分類 / 文章（含不是這個站點的）當 404；網路或系統錯誤往上丟讓 Next 回 500。
 * service.js：HTTP 200 的業務錯誤帶 isResponseError，非 200 帶 status（後端查無資料回 404）。
 */
export const isHelpNotFoundError = (error: unknown) => {
  const e = error as { isResponseError?: boolean; status?: number }
  return Boolean(e?.isResponseError) || e?.status === 404
}

/** service.js 遇到 502 會回 null 讓 SSR 繼續；幫助中心頁面沒資料就沒得顯示，直接當系統錯誤 */
export const requireHelpData = <T>(data: T | null | undefined): T => {
  if (!data) throw new Error('help center api returned no data')
  return data
}

/** 只有使用者在網址指定 ?country_code= 時才沿用到站內連結 */
export const countryQueryOf = (queryCountry: unknown) => (typeof queryCountry === 'string' && queryCountry ? queryCountry.toUpperCase() : undefined)
